import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  ArrowLeft,
  Check,
  Lock,
  Shield,
  Loader2,
} from "lucide-react";
import SectionReveal from "@/components/SectionReveal";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import useSeo from "@/hooks/useSeo";

const PREVIOUS_SUPPORT_OPTIONS = [
  "Therapy or counselling",
  "Coaching",
  "Clinical hypnotherapy",
  "Group programs / retreats",
  "Somatic / body-based work",
  "Nothing formal — this is my first time",
  "Prefer not to say",
];

const MENOPAUSE_STAGES = [
  "Not sure",
  "Perimenopause",
  "Menopause",
  "Post-menopause",
  "Not applicable / prefer not to say",
];

const FREQUENCY_OPTIONS = [
  "Weekly",
  "Fortnightly",
  "Monthly",
  "Not sure — happy to be guided by Pip",
];

const HRT_OPTIONS = ["Yes", "No", "Considering it", "Prefer not to say"];

// ICF-aligned acknowledgments. All five must be True to submit.
const ACKS = [
  {
    key: "ack_scope",
    title: "Scope of practice",
    body:
      "I understand that coaching with Pip is distinct from therapy or counselling. Pip is a Strategic Life Coach (ICF Member, currently working toward the PCC credential) and separately a Clinical Hypnotherapist — these are two different modalities. Any hypnotherapy work would be discussed and contracted separately.",
  },
  {
    key: "ack_confidentiality",
    title: "Confidentiality",
    body:
      "I understand that everything I share during coaching is held in strict confidence, with the following exceptions required by professional standards and law: risk of harm to self, risk of harm to others, and court-ordered disclosure. Pip may also use anonymised, non-identifying reflections for her own professional supervision.",
  },
  {
    key: "ack_referral",
    title: "Referral protocol",
    body:
      "I understand that if mental health concerns arise which fall outside the scope of coaching, Pip will raise this with me openly, discuss appropriate referrals, and may pause or end the coaching arrangement if that is in my best interest.",
  },
  {
    key: "ack_cancellation",
    title: "Cancellation policy",
    body:
      "I understand that sessions require 24 hours' notice to reschedule without charge. Sessions cancelled within 24 hours may be charged in full at Pip's discretion.",
  },
  {
    key: "ack_data",
    title: "Data storage & privacy",
    body:
      "I consent to Pip storing the information I've shared in this intake securely for the purposes of our coaching work. I understand I can request access to my records or ask for them to be deleted at any time. Pip complies with the Australian Privacy Principles.",
  },
];

// Group form questions into 5 progressive-disclosure steps so the intake
// feels like a warm conversation rather than a wall of fields.
const STEPS = [
  { id: "about", label: "About you" },
  { id: "context", label: "What's happening" },
  { id: "health", label: "Health & context" },
  { id: "preferences", label: "How we'll work" },
  { id: "consent", label: "Acknowledgments" },
];

function ProgressBar({ currentIdx }) {
  return (
    <div className="mb-10">
      <div className="flex items-center justify-between mb-3">
        {STEPS.map((s, i) => {
          const done = i < currentIdx;
          const active = i === currentIdx;
          return (
            <div key={s.id} className="flex-1 flex items-center">
              <div
                className={`flex items-center justify-center w-9 h-9 rounded-full text-xs font-semibold transition-all ${
                  done
                    ? "bg-sage text-cream"
                    : active
                    ? "bg-ink text-cream ring-4 ring-ink/10"
                    : "bg-cream border border-hairline text-ink-soft/50"
                }`}
                data-testid={`intake-step-${s.id}-indicator`}
              >
                {done ? <Check size={14} strokeWidth={2.5} /> : i + 1}
              </div>
              {i < STEPS.length - 1 && (
                <div
                  className={`flex-1 h-0.5 mx-2 ${
                    i < currentIdx ? "bg-sage" : "bg-hairline"
                  }`}
                />
              )}
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.14em] text-ink-soft/70">
        {STEPS.map((s, i) => (
          <span
            key={s.id}
            className={`flex-1 text-center px-1 ${
              i === currentIdx ? "text-ink font-semibold" : ""
            }`}
          >
            {s.label}
          </span>
        ))}
      </div>
    </div>
  );
}

const inputBase =
  "w-full bg-white border border-hairline rounded-xl px-4 py-3 text-ink placeholder-ink-soft/50 focus:outline-none focus:border-sage focus:ring-2 focus:ring-sage/15 transition";
const labelBase =
  "block text-sm font-semibold text-ink mb-2";
const hintBase = "text-xs text-ink-soft/70 mb-2";

function Field({ label, hint, children, testId }) {
  return (
    <div data-testid={testId ? `intake-field-${testId}` : undefined}>
      <label className={labelBase}>{label}</label>
      {hint && <p className={hintBase}>{hint}</p>}
      {children}
    </div>
  );
}

export default function ClientIntake() {
  useSeo({
    title: "New Client Intake · Private Sessions with Pip Bennett",
    description:
      "Complete your new-client intake for private 1:1 coaching with Pip Bennett — Strategic Life Coach (ICF Member) & Clinical Hypnotherapist.",
    canonical: "https://pipbennett.com/private-sessions/intake",
  });

  const navigate = useNavigate();
  const [stepIdx, setStepIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submittedName, setSubmittedName] = useState("");
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    timezone: "",
    pronouns: "",
    date_of_birth: "",
    bringing_you_here: "",
    what_shifted_recently: "",
    three_month_vision: "",
    previous_support: [],
    previous_support_notes: "",
    menopause_stage: "",
    on_hrt: "",
    medications: "",
    health_notes: "",
    coaching_style: "",
    availability: "",
    session_frequency: "",
    anything_else: "",
    ack_scope: false,
    ack_confidentiality: false,
    ack_referral: false,
    ack_cancellation: false,
    ack_data: false,
  });

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const togglePreviousSupport = (opt) => {
    setForm((f) => {
      const has = f.previous_support.includes(opt);
      return {
        ...f,
        previous_support: has
          ? f.previous_support.filter((o) => o !== opt)
          : [...f.previous_support, opt],
      };
    });
  };

  const canAdvance = (() => {
    if (stepIdx === 0) {
      return (
        form.first_name.trim().length > 0 &&
        form.last_name.trim().length > 0 &&
        /^\S+@\S+\.\S+$/.test(form.email.trim())
      );
    }
    if (stepIdx === 4) {
      return (
        form.ack_scope &&
        form.ack_confidentiality &&
        form.ack_referral &&
        form.ack_cancellation &&
        form.ack_data
      );
    }
    return true; // steps 2/3/4 are optional
  })();

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/private-sessions/intake", form);
      setSubmittedName(form.first_name);
      toast.success(data?.message || "Your intake has arrived safely.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Something went wrong. Please try again or email pip@pipbennett.com."
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ─── SUCCESS STATE ────────────────────────────────────────────────────────
  if (submittedName) {
    return (
      <div data-testid="page-client-intake" className="bg-cream min-h-screen">
        <section className="max-w-2xl mx-auto px-6 md:px-10 py-20 md:py-28 text-center">
          <SectionReveal>
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-sage text-cream mb-6">
              <Check size={22} strokeWidth={1.8} />
            </div>
            <h1
              className="font-serif-display text-4xl md:text-5xl text-ink leading-tight"
              data-testid="intake-success-heading"
            >
              Your intake has arrived safely,{" "}
              <em className="italic text-sage">{submittedName}</em>.
            </h1>
            <p className="mt-8 text-ink-soft leading-relaxed text-lg">
              Thank you for taking the time to share where you are. Pip reads every
              intake herself — carefully, and with the same attention she will bring
              to your first session.
            </p>
            <p className="mt-5 text-ink-soft leading-relaxed">
              You will hear from Pip directly within two business days to confirm
              your first session and answer any questions you have. A copy of your
              intake confirmation is on its way to your inbox.
            </p>
            <div className="mt-12 pt-10 border-t border-hairline">
              <p className="text-sm text-ink-soft/80 mb-5">
                While you wait, you might like:
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  to="/podcast"
                  className="inline-flex items-center justify-center gap-2 border border-sage text-sage hover:bg-sage hover:text-cream rounded-full px-6 py-3 font-medium text-sm transition"
                  data-testid="intake-success-podcast-cta"
                >
                  Listen to the Podcast
                  <ArrowRight size={14} strokeWidth={1.8} />
                </Link>
                <Link
                  to="/insights"
                  className="inline-flex items-center justify-center gap-2 text-ink-soft hover:text-ink font-medium text-sm transition px-4 py-3"
                  data-testid="intake-success-journal-cta"
                >
                  Read The Journal
                </Link>
              </div>
            </div>
          </SectionReveal>
        </section>
      </div>
    );
  }

  // ─── FORM ─────────────────────────────────────────────────────────────────
  return (
    <div data-testid="page-client-intake" className="bg-cream min-h-screen">
      {/* HERO */}
      <section className="pt-14 md:pt-20 pb-8 md:pb-10">
        <div className="max-w-3xl mx-auto px-6 md:px-10 text-center">
          <SectionReveal>
            <div className="eyebrow mb-4 text-gold-dark inline-flex items-center gap-2">
              <Lock size={11} strokeWidth={2} />
              Private &amp; Confidential
            </div>
            <h1 className="font-serif-display text-4xl md:text-5xl lg:text-[3.4rem] text-ink leading-[1.05]">
              A soft, honest place to <em className="italic text-sage">begin</em>.
            </h1>
            <p className="mt-6 max-w-xl mx-auto text-ink-soft leading-relaxed">
              This intake is where we start. It gives Pip a sense of where you are,
              what has been building, and what you might want the next season to be
              built around — so your first session can arrive somewhere useful,
              rather than starting from scratch.
            </p>
            <p className="mt-3 text-xs text-ink-soft/70">
              Approximately 10–12 minutes · All questions except the essentials are
              optional
            </p>
          </SectionReveal>
        </div>
      </section>

      {/* FORM CARD */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <div className="bg-cream-soft border border-hairline rounded-3xl p-6 md:p-10">
            <ProgressBar currentIdx={stepIdx} />

            {/* STEP 1 — ABOUT YOU */}
            {stepIdx === 0 && (
              <div className="space-y-6" data-testid="intake-step-about">
                <div>
                  <h2 className="font-serif-display text-2xl md:text-3xl text-ink leading-tight">
                    Let&rsquo;s start with the basics.
                  </h2>
                  <p className="mt-2 text-ink-soft text-sm">
                    Only the first four fields are required.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="First name *" testId="first-name">
                    <input
                      type="text"
                      className={inputBase}
                      value={form.first_name}
                      onChange={(e) => set({ first_name: e.target.value })}
                      data-testid="intake-first-name"
                      required
                    />
                  </Field>
                  <Field label="Last name *" testId="last-name">
                    <input
                      type="text"
                      className={inputBase}
                      value={form.last_name}
                      onChange={(e) => set({ last_name: e.target.value })}
                      data-testid="intake-last-name"
                      required
                    />
                  </Field>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Email *" testId="email">
                    <input
                      type="email"
                      className={inputBase}
                      value={form.email}
                      onChange={(e) => set({ email: e.target.value })}
                      data-testid="intake-email"
                      required
                    />
                  </Field>
                  <Field label="Phone (optional)" testId="phone">
                    <input
                      type="tel"
                      className={inputBase}
                      value={form.phone}
                      onChange={(e) => set({ phone: e.target.value })}
                      data-testid="intake-phone"
                    />
                  </Field>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Field label="Pronouns" hint="e.g. she/her" testId="pronouns">
                    <input
                      type="text"
                      className={inputBase}
                      value={form.pronouns}
                      onChange={(e) => set({ pronouns: e.target.value })}
                      data-testid="intake-pronouns"
                    />
                  </Field>
                  <Field label="Timezone" hint="e.g. AEST, GMT" testId="timezone">
                    <input
                      type="text"
                      className={inputBase}
                      value={form.timezone}
                      onChange={(e) => set({ timezone: e.target.value })}
                      data-testid="intake-timezone"
                    />
                  </Field>
                  <Field
                    label="Date of birth"
                    hint="Optional"
                    testId="dob"
                  >
                    <input
                      type="text"
                      placeholder="DD / MM / YYYY"
                      className={inputBase}
                      value={form.date_of_birth}
                      onChange={(e) => set({ date_of_birth: e.target.value })}
                      data-testid="intake-dob"
                    />
                  </Field>
                </div>
              </div>
            )}

            {/* STEP 2 — CONTEXT */}
            {stepIdx === 1 && (
              <div className="space-y-6" data-testid="intake-step-context">
                <div>
                  <h2 className="font-serif-display text-2xl md:text-3xl text-ink leading-tight">
                    What&rsquo;s bringing you here?
                  </h2>
                  <p className="mt-2 text-ink-soft text-sm">
                    Answer whatever lands most honestly. Nothing has to be perfectly
                    worded.
                  </p>
                </div>

                <Field
                  label="What&rsquo;s bringing you to coaching now?"
                  testId="bringing-you-here"
                >
                  <textarea
                    className={inputBase}
                    rows={4}
                    value={form.bringing_you_here}
                    onChange={(e) => set({ bringing_you_here: e.target.value })}
                    data-testid="intake-bringing"
                  />
                </Field>

                <Field
                  label="What has shifted recently that made you reach out?"
                  testId="what-shifted"
                >
                  <textarea
                    className={inputBase}
                    rows={4}
                    value={form.what_shifted_recently}
                    onChange={(e) => set({ what_shifted_recently: e.target.value })}
                    data-testid="intake-shifted"
                  />
                </Field>

                <Field
                  label="If we did really good work together, what would be different in your life three months from now?"
                  testId="three-month"
                >
                  <textarea
                    className={inputBase}
                    rows={4}
                    value={form.three_month_vision}
                    onChange={(e) => set({ three_month_vision: e.target.value })}
                    data-testid="intake-vision"
                  />
                </Field>

                <Field
                  label="Have you worked with any of these before?"
                  hint="Tick any that apply"
                  testId="previous-support"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {PREVIOUS_SUPPORT_OPTIONS.map((opt) => {
                      const selected = form.previous_support.includes(opt);
                      return (
                        <button
                          type="button"
                          key={opt}
                          onClick={() => togglePreviousSupport(opt)}
                          className={`text-left text-sm px-4 py-3 rounded-xl border transition ${
                            selected
                              ? "bg-sage text-cream border-sage"
                              : "bg-white border-hairline text-ink hover:border-sage"
                          }`}
                          data-testid={`intake-previous-${opt.split(" ")[0].toLowerCase()}`}
                        >
                          <span className="inline-flex items-center gap-2">
                            <span
                              className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                                selected
                                  ? "bg-cream border-cream text-sage"
                                  : "border-ink-soft/30"
                              }`}
                            >
                              {selected && <Check size={11} strokeWidth={3} />}
                            </span>
                            {opt}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </Field>

                <Field
                  label="Anything you&rsquo;d like to add about your prior experience?"
                  testId="previous-notes"
                >
                  <textarea
                    className={inputBase}
                    rows={3}
                    value={form.previous_support_notes}
                    onChange={(e) => set({ previous_support_notes: e.target.value })}
                    data-testid="intake-previous-notes"
                  />
                </Field>
              </div>
            )}

            {/* STEP 3 — HEALTH & CONTEXT */}
            {stepIdx === 2 && (
              <div className="space-y-6" data-testid="intake-step-health">
                <div>
                  <h2 className="font-serif-display text-2xl md:text-3xl text-ink leading-tight">
                    Health &amp; context.
                  </h2>
                  <p className="mt-2 text-ink-soft text-sm">
                    All of this is optional — share whatever feels relevant. It helps
                    Pip meet you where you are.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Menopause stage" testId="menopause-stage">
                    <select
                      className={inputBase}
                      value={form.menopause_stage}
                      onChange={(e) => set({ menopause_stage: e.target.value })}
                      data-testid="intake-menopause-stage"
                    >
                      <option value="">Select…</option>
                      {MENOPAUSE_STAGES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="On HRT?" testId="hrt">
                    <select
                      className={inputBase}
                      value={form.on_hrt}
                      onChange={(e) => set({ on_hrt: e.target.value })}
                      data-testid="intake-hrt"
                    >
                      <option value="">Select…</option>
                      {HRT_OPTIONS.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>

                <Field
                  label="Current medications or supplements"
                  hint="Optional — anything you&rsquo;d like Pip to know about"
                  testId="medications"
                >
                  <textarea
                    className={inputBase}
                    rows={2}
                    value={form.medications}
                    onChange={(e) => set({ medications: e.target.value })}
                    data-testid="intake-medications"
                  />
                </Field>

                <Field
                  label="Any health conditions, mental health context, or trauma history you'd like Pip to know?"
                  hint="Safe, confidential, and never mandatory to answer."
                  testId="health-notes"
                >
                  <textarea
                    className={inputBase}
                    rows={4}
                    value={form.health_notes}
                    onChange={(e) => set({ health_notes: e.target.value })}
                    data-testid="intake-health-notes"
                  />
                </Field>
              </div>
            )}

            {/* STEP 4 — PREFERENCES */}
            {stepIdx === 3 && (
              <div className="space-y-6" data-testid="intake-step-preferences">
                <div>
                  <h2 className="font-serif-display text-2xl md:text-3xl text-ink leading-tight">
                    How we&rsquo;ll work together.
                  </h2>
                  <p className="mt-2 text-ink-soft text-sm">
                    A few practical preferences so Pip can propose a rhythm that
                    works for you.
                  </p>
                </div>

                <Field
                  label="What kind of coaching support tends to work best for you?"
                  hint="e.g. Direct and challenging · Gentle and reflective · Somewhere in between"
                  testId="style"
                >
                  <textarea
                    className={inputBase}
                    rows={3}
                    value={form.coaching_style}
                    onChange={(e) => set({ coaching_style: e.target.value })}
                    data-testid="intake-style"
                  />
                </Field>

                <Field
                  label="Availability"
                  hint="e.g. Weekday mornings AEST; Tues/Thurs after 3pm"
                  testId="availability"
                >
                  <textarea
                    className={inputBase}
                    rows={3}
                    value={form.availability}
                    onChange={(e) => set({ availability: e.target.value })}
                    data-testid="intake-availability"
                  />
                </Field>

                <Field label="Preferred session frequency" testId="frequency">
                  <select
                    className={inputBase}
                    value={form.session_frequency}
                    onChange={(e) => set({ session_frequency: e.target.value })}
                    data-testid="intake-frequency"
                  >
                    <option value="">Select…</option>
                    {FREQUENCY_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field
                  label="Anything else you&rsquo;d like Pip to know before we begin?"
                  testId="anything-else"
                >
                  <textarea
                    className={inputBase}
                    rows={4}
                    value={form.anything_else}
                    onChange={(e) => set({ anything_else: e.target.value })}
                    data-testid="intake-anything-else"
                  />
                </Field>
              </div>
            )}

            {/* STEP 5 — ACKNOWLEDGMENTS */}
            {stepIdx === 4 && (
              <div className="space-y-6" data-testid="intake-step-consent">
                <div>
                  <h2 className="font-serif-display text-2xl md:text-3xl text-ink leading-tight">
                    A few important acknowledgments.
                  </h2>
                  <p className="mt-2 text-ink-soft text-sm">
                    These are here for your protection and mine — a shared
                    understanding of how we work together. All five are required to
                    submit.
                  </p>
                </div>

                <div className="space-y-3">
                  {ACKS.map((a) => (
                    <label
                      key={a.key}
                      className={`flex items-start gap-4 bg-white border rounded-2xl p-5 cursor-pointer transition ${
                        form[a.key]
                          ? "border-sage bg-sage/5"
                          : "border-hairline hover:border-sage/40"
                      }`}
                      data-testid={`intake-ack-${a.key}`}
                    >
                      <input
                        type="checkbox"
                        checked={form[a.key]}
                        onChange={(e) => set({ [a.key]: e.target.checked })}
                        className="mt-1 h-5 w-5 accent-sage flex-shrink-0"
                        data-testid={`intake-ack-${a.key}-checkbox`}
                      />
                      <div>
                        <div className="font-semibold text-ink text-sm mb-1.5">
                          {a.title}
                        </div>
                        <p className="text-ink-soft text-sm leading-relaxed">
                          {a.body}
                        </p>
                      </div>
                    </label>
                  ))}
                </div>

                <p className="text-xs text-ink-soft/70 mt-4 flex items-start gap-2">
                  <Lock size={12} strokeWidth={1.6} className="mt-0.5 flex-shrink-0" />
                  Your intake is transmitted over an encrypted connection and stored
                  privately. Only Pip has access.
                </p>
              </div>
            )}

            {/* NAV */}
            <div className="mt-10 pt-6 border-t border-hairline flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-3">
              {stepIdx > 0 ? (
                <button
                  type="button"
                  onClick={() => setStepIdx((i) => Math.max(0, i - 1))}
                  className="inline-flex items-center justify-center gap-2 text-ink-soft hover:text-ink font-medium text-sm transition px-5 py-3"
                  data-testid="intake-prev"
                >
                  <ArrowLeft size={14} strokeWidth={1.8} />
                  Back
                </button>
              ) : (
                <div />
              )}

              {stepIdx < STEPS.length - 1 ? (
                <button
                  type="button"
                  onClick={() => setStepIdx((i) => Math.min(STEPS.length - 1, i + 1))}
                  disabled={!canAdvance}
                  className="inline-flex items-center justify-center gap-2 bg-sage hover:bg-sage-dark disabled:opacity-40 disabled:cursor-not-allowed text-cream rounded-full px-7 py-3.5 font-medium tracking-wide transition-all"
                  data-testid="intake-next"
                >
                  Continue
                  <ArrowRight size={14} strokeWidth={2} />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={submit}
                  disabled={!canAdvance || submitting}
                  className="inline-flex items-center justify-center gap-2 bg-ink hover:bg-ink/90 disabled:opacity-50 disabled:cursor-not-allowed text-cream rounded-full px-8 py-4 font-bold tracking-wide transition-all"
                  data-testid="intake-submit"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} strokeWidth={1.8} className="animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      Send my intake to Pip
                      <ArrowRight size={16} strokeWidth={2} />
                    </>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* CREDENTIAL FOOTER */}
          <div className="mt-8 text-center text-xs text-ink-soft/70 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4">
            <span className="inline-flex items-center gap-1.5">
              <Shield size={12} strokeWidth={1.6} />
              ICF Member · Strategic Life Coach
            </span>
            <span className="hidden sm:inline text-ink-soft/40">·</span>
            <span>Clinical Hypnotherapist</span>
            <span className="hidden sm:inline text-ink-soft/40">·</span>
            <button
              type="button"
              onClick={() => navigate("/private-sessions")}
              className="hover:text-ink transition underline underline-offset-2"
              data-testid="intake-back-to-sales"
            >
              Back to Private Sessions
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
